import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import TicketDetailModal from './TicketDetailModal';
import { supabase } from 'components/supabase/supabaseClient';
import AddResponseModal from './AddReponseModal'; // ✅ Import the new modal

const TicketAnalyticsTable = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
   const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [tickets, setTickets] = useState([]);

  // Fetch tickets from Supabase
  useEffect(() => {
    const fetchTickets = async () => {
    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tickets:', error.message);
      } else {
        setTickets(data || []);
      }
    };

    fetchTickets();
  }, []);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };
  const handleAddResponse = (ticket) => {     // ✅ new handler
      setSelectedTicket(ticket);
      setIsResponseModalOpen(true);
  };
  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleUpdateTicket = (updatedTicket) => {
    setTickets((prevTickets) =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? { ...ticket, ...updatedTicket } : ticket
      )
    );
  };

  const handleAssignTicket = (ticketId, assignee) => {
    setTickets((prevTickets) =>
      prevTickets.map(ticket =>
        ticket.id === ticketId ? { ...ticket, assignee, lastUpdate: new Date().toISOString() } : ticket
      )
    );
  };

  // 🔽 Your color/status helper functions stay same...

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error bg-error/10 border-error/20';
      case 'high': return 'text-warning bg-warning/10 border-warning/20';
      case 'medium': return 'text-primary bg-primary/10 border-primary/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-error bg-error/10 border-error/20';
      case 'in-progress': return 'text-warning bg-warning/10 border-warning/20';
      case 'pending': return 'text-text-secondary bg-muted border-border';
      case 'resolved': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getSlaStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'breach': return 'text-error';
      case 'at-risk': return 'text-warning';
      case 'on-track': return 'text-success';
      case 'met': return 'text-success';
      default: return 'text-text-secondary';
    }
  };

  const getSlaIcon = (slaStatus) => {
    switch (slaStatus) {
      case 'breach': return 'AlertTriangle';
      case 'at-risk': return 'Clock';
      case 'on-track': return 'CheckCircle';
      case 'met': return 'CheckCircle2';
      default: return 'Clock';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch =
      ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPriority = selectedPriority === 'all' || ticket.priority === selectedPriority;
    const matchesStatus = selectedStatus === 'all' || ticket.status === selectedStatus;

    return matchesSearch && matchesPriority && matchesStatus;
  });


  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-elevation-1 dashboard-fade-in">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Table" size={20} className="text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Ticket Analytics</h3>
                <p className="text-sm text-text-secondary">Comprehensive case management and tracking</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Export CSV
              </Button>
              <Button variant="default" size="sm" iconName="Plus">
                New Ticket
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Search tickets, customers, or IDs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Ticket</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Customer</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Priority</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Status</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Assignee</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">SLA</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Last Update</th>
                <th className="text-left p-4 text-sm font-medium text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="border-b border-border hover:bg-muted/20 transition-colors duration-150">
                  <td className="p-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-text-primary">{ticket.id}</span>
                        {ticket.escalationLevel > 0 && (
                          <div className="flex items-center space-x-1">
                            <Icon name="ArrowUp" size={12} className="text-error" />
                            <span className="text-xs text-error font-medium">L{ticket.escalationLevel}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary line-clamp-2 max-w-xs">{ticket.title}</p>
                      <span className="inline-block mt-1 px-2 py-1 bg-muted rounded text-xs text-text-secondary">
                        {ticket.category}
                      </span>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <span className="text-sm font-medium text-text-primary">{ticket.customer}</span>
                  </td>
                                    
                  <td className="p-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                        getPriorityColor(ticket.priority || "low") // default low
                      }`}
                    >
                      {(ticket.priority || "low")
                        .charAt(0)
                        .toUpperCase() + (ticket.priority || "low").slice(1)}
                    </span>
                  </td>
                  
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <img
                        src={ticket.assigneeAvatar}
                        alt={ticket.assignee}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-sm text-text-primary">{ticket.assignee}</span>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      <Icon 
                        name={getSlaIcon(ticket.status)} 
                        size={14} 
                        className={getSlaStatusColor(ticket.status)} 
                      />
                      <span className={`text-xs font-medium ${getSlaStatusColor(ticket.status)}`}>
                        {ticket.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <span className="text-sm text-text-secondary">{formatTimeAgo(ticket.lastUpdate)}</span>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-1"
                        onClick={() => handleViewTicket(ticket)}
                        title="View Ticket"
                      >
                        <Icon name="Eye" size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-1"
                        onClick={() => handleEditTicket(ticket)}
                        title="Edit Ticket"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-1"
                        onClick={() => handleAddResponse(ticket)}
                        title="Add Response"
                      >
                        <Icon name="MessageSquare" size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTickets.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No tickets found matching your criteria</p>
          </div>
        )}

        <div className="p-4 border-t border-border bg-muted/20">
          <div className="flex items-center justify-between text-sm text-text-secondary">
            <span>Showing {filteredTickets.length} of {tickets.length} tickets</span>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" disabled>
                <Icon name="ChevronLeft" size={16} />
              </Button>
              <span className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs">1</span>
              <Button variant="ghost" size="sm" disabled>
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTicket(null);
        }}
        onUpdate={handleUpdateTicket}
      />
       {/* Add Response Modal */}
      <AddResponseModal
        ticket={selectedTicket}
        isOpen={isResponseModalOpen}
        onClose={() => {
          setIsResponseModalOpen(false);
          setSelectedTicket(null);
        }}
        onUpdate={handleUpdateTicket}
      />
    </>
  );
};

export default TicketAnalyticsTable;